import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, QrCode, CheckCircle, XCircle, Users, Ticket, Search, Camera } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CheckinPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem("adminToken"));
  const [password, setPassword] = useState("");
  const [stats, setStats] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (loggedIn) {
      fetchStats();
      const interval = setInterval(fetchStats, 10000); // Actualizar cada 10 segundos
      return () => clearInterval(interval);
    }
  }, [loggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/admin/login`, { password });
      localStorage.setItem("adminToken", response.data.token);
      setToken(response.data.token);
      setLoggedIn(true);
      toast.success("Sesión iniciada");
    } catch (error) {
      toast.error("Contraseña incorrecta");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/checkin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error obteniendo stats:", error);
    }
  };

  const verificarQR = async (qrData) => {
    if (!qrData || loading) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/checkin/verificar?qr_data=${encodeURIComponent(qrData)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setLastResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchStats();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Error verificando QR");
      setLastResult({
        success: false,
        message: "Error de conexión",
        boleto: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      verificarQR(manualCode.trim());
      setManualCode("");
    }
  };

  // Iniciar cámara para escaneo
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setScanning(true);
        scanQRCode();
      }
    } catch (error) {
      toast.error("No se pudo acceder a la cámara");
      console.error("Error accediendo a cámara:", error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setScanning(false);
  };

  const scanQRCode = () => {
    // Nota: Para producción se recomienda usar una librería como jsQR o html5-qrcode
    // Por ahora usamos entrada manual
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-warm-bone to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8">
          <div className="text-center mb-8">
            <QrCode className="h-16 w-16 text-zuno-wine mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-zuno-wine">Check-in del Evento</h1>
            <p className="text-slate-600">Ingresa la contraseña de administrador</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              data-testid="checkin-password"
            />
            <Button type="submit" className="w-full btn-primary" data-testid="checkin-login-btn">
              Iniciar Sesión
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-zuno-wine/90">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="text-white hover:text-white/80"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="mr-2" size={20} />
            Admin
          </Button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <QrCode size={24} />
            Check-in
          </h1>
          <div className="w-24"></div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-white/10 backdrop-blur border-white/20 text-center">
              <Ticket className="h-8 w-8 text-blue-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{stats.total_boletos}</p>
              <p className="text-sm text-white/70">Total Boletos</p>
            </Card>
            <Card className="p-4 bg-green-500/20 backdrop-blur border-green-400/30 text-center">
              <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-green-400">{stats.boletos_usados}</p>
              <p className="text-sm text-green-300/70">Check-ins</p>
            </Card>
            <Card className="p-4 bg-amber-500/20 backdrop-blur border-amber-400/30 text-center">
              <Users className="h-8 w-8 text-amber-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-amber-400">{stats.boletos_pendientes}</p>
              <p className="text-sm text-amber-300/70">Pendientes</p>
            </Card>
            <Card className="p-4 bg-purple-500/20 backdrop-blur border-purple-400/30 text-center">
              <div className="h-8 w-8 text-purple-400 mx-auto mb-2 flex items-center justify-center text-2xl font-bold">%</div>
              <p className="text-3xl font-bold text-purple-400">{stats.porcentaje_asistencia}%</p>
              <p className="text-sm text-purple-300/70">Asistencia</p>
            </Card>
          </div>
        )}

        {/* Scanner Section */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Manual Input */}
          <Card className="p-6 bg-white/10 backdrop-blur border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Search size={24} />
              Ingresar Código Manualmente
            </h2>
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <Input
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="BOLETO|ZUNO12345678|email@ejemplo.com|id"
                className="bg-white/10 border-white/30 text-white placeholder:text-white/50"
                data-testid="manual-qr-input"
              />
              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={loading || !manualCode.trim()}
                data-testid="verificar-qr-btn"
              >
                {loading ? "Verificando..." : "Verificar Boleto"}
              </Button>
            </form>

            <div className="mt-4 p-4 bg-slate-800/50 rounded-lg">
              <p className="text-sm text-white/70">
                <strong>Formato del QR:</strong><br/>
                BOLETO|NUMERO_BOLETO|EMAIL|ID_COMPRADOR
              </p>
            </div>
          </Card>

          {/* Result Display */}
          <Card className={`p-6 backdrop-blur border-white/20 ${
            lastResult?.success ? 'bg-green-500/20' : 
            lastResult?.success === false ? 'bg-red-500/20' : 'bg-white/10'
          }`}>
            <h2 className="text-xl font-bold text-white mb-4">Resultado</h2>
            
            {lastResult ? (
              <div className="space-y-4">
                <div className={`p-6 rounded-xl text-center ${
                  lastResult.success ? 'bg-green-500/30' : 'bg-red-500/30'
                }`}>
                  {lastResult.success ? (
                    <CheckCircle className="h-20 w-20 text-green-400 mx-auto mb-4" />
                  ) : (
                    <XCircle className="h-20 w-20 text-red-400 mx-auto mb-4" />
                  )}
                  <p className={`text-2xl font-bold ${lastResult.success ? 'text-green-400' : 'text-red-400'}`}>
                    {lastResult.message}
                  </p>
                </div>

                {lastResult.boleto && (
                  <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
                    <p className="text-white">
                      <strong>Boleto:</strong> {lastResult.boleto.numero_boleto}
                    </p>
                    <p className="text-white">
                      <strong>Nombre:</strong> {lastResult.boleto.nombre_asistente}
                    </p>
                    <p className="text-white">
                      <strong>Tipo:</strong> {lastResult.boleto.tipo_asistente}
                    </p>
                    <p className={`font-semibold ${
                      lastResult.boleto.estado === 'usado' ? 'text-amber-400' : 'text-green-400'
                    }`}>
                      <strong>Estado:</strong> {lastResult.boleto.estado}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-white/50">
                <QrCode className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Escanea o ingresa un código QR para verificar</p>
              </div>
            )}
          </Card>
        </div>

        {/* Recent Check-ins */}
        {stats?.ultimos_checkins?.length > 0 && (
          <Card className="mt-8 p-6 bg-white/10 backdrop-blur border-white/20">
            <h2 className="text-xl font-bold text-white mb-4">Últimos Check-ins</h2>
            <div className="space-y-2">
              {stats.ultimos_checkins.map((checkin, index) => (
                <div 
                  key={checkin.numero_boleto || `checkin-${index}`}
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                >
                  <div>
                    <p className="text-white font-semibold">{checkin.nombre_asistente}</p>
                    <p className="text-sm text-white/60">{checkin.numero_boleto} - {checkin.tipo_asistente}</p>
                  </div>
                  <div className="text-right">
                    <CheckCircle className="h-5 w-5 text-green-400 inline mr-2" />
                    <span className="text-sm text-white/60">
                      {checkin.usado_en ? new Date(checkin.usado_en).toLocaleTimeString() : '-'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CheckinPage;
